import {
  createContext,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import NotificationPanel from "@/components/NotificationPanel/NotificationPanel";
import type { NotificationLog } from "@/services/api/userProfile";

interface NotificationContextValue {
  notificationCount: number;
  notifications: NotificationLog[];
  isNotificationOpen: boolean;
  isNotificationLoading: boolean;
  openNotifications: () => void;
  closeNotifications: () => void;
  refreshNotifications: () => Promise<void>;
}

const NotificationContext = createContext<NotificationContextValue | undefined>(
  undefined,
);

export const NotificationProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [notifications] = useState<NotificationLog[]>([]);
  const [notificationCount] = useState(0);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);

  const contextValue = useMemo(
    () => ({
      notificationCount,
      notifications,
      isNotificationOpen,
      isNotificationLoading: false,
      openNotifications: () => setIsNotificationOpen(true),
      closeNotifications: () => setIsNotificationOpen(false),
      refreshNotifications: async () => {},
    }),
    [isNotificationOpen, notificationCount, notifications],
  );

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
      <NotificationPanel
        isLoading={false}
        notifications={notifications}
        onClose={() => setIsNotificationOpen(false)}
        open={isNotificationOpen}
        unreadCount={notificationCount}
      />
    </NotificationContext.Provider>
  );
};

export const useNotificationCenter = () => {
  const context = useContext(NotificationContext);

  if (!context) {
    throw new Error(
      "useNotificationCenter must be used within a NotificationProvider",
    );
  }

  return context;
};
